import { useState, useEffect } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSuggestPolicies, useApplyPolicySuggestions } from '@/hooks/useIdentityProviders';
import type { PolicySuggestion, ApplyPolicySuggestionsResponse } from '@/api/identity-providers';

interface PolicySuggestionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  idpId: string;
  idpName: string;
}

type DialogStep = 'loading' | 'review' | 'applying' | 'done';

/** Renders a color-coded confidence percentage badge. */
function ConfidenceBadge({ confidence }: { confidence: number }) {
  const pct = (confidence * 100).toFixed(0) + '%';
  if (confidence >= 0.8) {
    return (
      <span className="text-xs font-medium text-status-allow bg-status-allow/10 rounded px-1.5 py-0.5 tabular">
        {pct}
      </span>
    );
  }
  if (confidence >= 0.5) {
    return (
      <span className="text-xs font-medium text-status-warning bg-status-warning/10 rounded px-1.5 py-0.5 tabular">
        {pct}
      </span>
    );
  }
  return (
    <span className="text-xs font-medium text-destructive bg-destructive/10 rounded px-1.5 py-0.5 tabular">
      {pct}
    </span>
  );
}

/** Returns a variant string for the category badge. */
function categoryVariant(category: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (category) {
    case 'rbac':
      return 'default';
    case 'abac':
      return 'secondary';
    case 'deny':
      return 'destructive';
    default:
      return 'outline';
  }
}

export function PolicySuggestionDialog({
  open,
  onOpenChange,
  idpId,
  idpName,
}: PolicySuggestionDialogProps) {
  const [step, setStep] = useState<DialogStep>('loading');
  const [suggestions, setSuggestions] = useState<PolicySuggestion[]>([]);
  const [analysisSum, setAnalysisSum] = useState('');
  const [principalCount, setPrincipalCount] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [applyResult, setApplyResult] = useState<ApplyPolicySuggestionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const suggestMutation = useSuggestPolicies();
  const applyMutation = useApplyPolicySuggestions();

  // Fetch suggestions whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setStep('loading');
    setError(null);
    setSuggestions([]);
    setSelected(new Set());
    setApplyResult(null);

    suggestMutation.mutate(idpId, {
      onSuccess: (result) => {
        setSuggestions(result.suggestions);
        setPrincipalCount(result.principal_count);
        setAnalysisSum(result.analysis_summary);
        setSelected(new Set(result.suggestions.map((s) => s.suggestion_id)));
        setStep('review');
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Failed to generate policy suggestions');
        setStep('review');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idpId]);

  function toggleSelection(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(suggestions.map((s) => s.suggestion_id)));
  }

  function selectNone() {
    setSelected(new Set());
  }

  function handleApply() {
    setStep('applying');
    setError(null);
    const selectedSuggestions = suggestions.filter((s) => selected.has(s.suggestion_id));
    applyMutation.mutate(
      {
        idpId,
        suggestionIds: selectedSuggestions.map((s) => s.suggestion_id),
        suggestionsData: selectedSuggestions,
      },
      {
        onSuccess: (result) => {
          setApplyResult(result);
          setStep('done');
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'Failed to apply policy suggestions');
          setStep('review');
        },
      }
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Recommended Starting Points</DialogTitle>
          <DialogDescription>
            Policy suggestions for <span className="font-medium text-foreground">{idpName}</span> based on
            principal group membership and attribute patterns.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 py-2">
          {error && (
            <div className="mb-4 rounded bg-destructive/10 p-3 text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">Analyzing principals and generating suggestions...</p>
            </div>
          )}

          {step === 'review' && !error && suggestions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
              <Users className="h-10 w-10" />
              <p className="font-medium text-foreground">No suggestions generated</p>
              <p className="text-sm">
                {principalCount === 0
                  ? 'This IDP has no synced principals yet. Run a sync first.'
                  : 'No policy patterns were detected in the current principal set.'}
              </p>
            </div>
          )}

          {step === 'review' && suggestions.length > 0 && (
            <div className="space-y-4">
              {analysisSum && (
                <p className="text-sm text-muted-foreground">{analysisSum}</p>
              )}
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''} &middot;{' '}
                  {principalCount} principal{principalCount !== 1 ? 's' : ''} analysed
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={selectNone}>
                    Select None
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {suggestions.map((s) => {
                  const isSelected = selected.has(s.suggestion_id);
                  return (
                    <div
                      key={s.suggestion_id}
                      className={`rounded-lg border p-4 cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/40'
                      }`}
                      onClick={() => toggleSelection(s.suggestion_id)}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(s.suggestion_id)}
                          className="mt-1 rounded border-input flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-sm">{s.name}</span>
                            <Badge variant={categoryVariant(s.category)} className="text-xs capitalize">
                              {s.category}
                            </Badge>
                            <ConfidenceBadge confidence={s.confidence} />
                            {s.member_count > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {s.member_count} member{s.member_count !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{s.description}</p>
                          {s.reasoning && (
                            <p className="text-xs text-muted-foreground/70 italic">{s.reasoning}</p>
                          )}
                          {s.sample_principals.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              Sample principals:{' '}
                              <span className="font-mono">{s.sample_principals.slice(0, 5).join(', ')}</span>
                              {s.sample_principals.length > 5 && (
                                <span> &amp; {s.sample_principals.length - 5} more</span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'applying' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">Creating draft policies...</p>
            </div>
          )}

          {step === 'done' && applyResult && (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <CheckCircle2 className="h-12 w-12 text-status-allow" />
              <p className="font-semibold">Policies Created</p>
              <div className="text-sm text-muted-foreground space-y-1 text-center">
                <p>
                  {applyResult.applied} draft {applyResult.applied === 1 ? 'policy' : 'policies'} created
                </p>
                {applyResult.errors.length > 0 && (
                  <p className="text-destructive">
                    {applyResult.errors.length} suggestion{applyResult.errors.length !== 1 ? 's' : ''} failed
                  </p>
                )}
              </div>
              {applyResult.policies.length > 0 && (
                <ul className="mt-2 w-full max-w-sm space-y-1">
                  {applyResult.policies.map((p) => (
                    <li
                      key={p.suggestion_id}
                      className="text-xs flex items-center justify-between border rounded px-2 py-1"
                    >
                      <span className="truncate font-medium">{p.policy_name}</span>
                      <span className="ml-2 text-muted-foreground capitalize">{p.status}</span>
                    </li>
                  ))}
                </ul>
              )}
              {applyResult.errors.length > 0 && (
                <ul className="mt-1 w-full max-w-sm space-y-1">
                  {applyResult.errors.map((e) => (
                    <li
                      key={e.suggestion_id}
                      className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1"
                    >
                      {e.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          {step === 'done' ? (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {step === 'review' && suggestions.length > 0 && (
                <Button onClick={handleApply} disabled={selected.size === 0}>
                  Apply {selected.size} Selected
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
